import type { ApiClientError } from "@/lib/api/client";
import type { PermissionContext } from "@/lib/permissions";

interface ToastFn {
  (props: {
    title: string;
    description: string;
    variant?: "default" | "destructive";
  }): void;
}

export interface HandlePermissionErrorOptions {
  /** The `requestApproval` function from `useApproval()` context. */
  requestApproval: (
    response: Response,
    data: Record<string, unknown>,
    retryFn: (approvalToken: string) => Promise<void>,
    options?: {
      context?: PermissionContext;
      actionDescription?: string;
    }
  ) => boolean;
  /** Toast function from `useToast()`. */
  toast: ToastFn;
  /** Function to retry the original request with the approval token. */
  retryFn: (approvalToken: string) => Promise<void>;
  /** Human-readable description of the action (e.g., "Create dispense"). */
  actionDescription?: string;
  /** Permission context (e.g., drug schedule). */
  context?: PermissionContext;
}

/**
 * Handle permission-related API errors (403 responses).
 *
 * Accepts an ApiClientError and checks for approval-required or denied status.
 *
 * - If `requires_approval`: opens the global approval modal via `requestApproval`
 * - If denied (403 without `requiresApproval`): shows a warning toast
 * - If not a 403 error: returns `false` so the caller can handle it
 *
 * @returns `true` if the error was handled (caller should stop), `false` if not a permission error
 */
export function handlePermissionError(
  error: ApiClientError,
  options: HandlePermissionErrorOptions
): boolean {
  if (error.statusCode !== 403) {
    return false;
  }

  const data = error.data;
  const { requestApproval, toast, retryFn, actionDescription, context } =
    options;

  // Case 1: requires approval — open the global modal
  if (data.requiresApproval === true) {
    const fakeResponse = new Response(JSON.stringify(data), {
      status: 403,
      headers: { "Content-Type": "application/json" },
    });
    const opened = requestApproval(fakeResponse, data, retryFn, {
      context,
      actionDescription,
    });
    if (opened) {
      return true;
    }
  }

  // Case 2: denied — show user-friendly toast (never expose raw API messages)
  toast({
    title: "Permission Denied",
    description:
      "You don't have permission to perform this action. Contact your administrator for access.",
    variant: "destructive",
  });

  return true;
}
